import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Siren, ScanSearch } from 'lucide-react';
import { useIncidents, createIncident, runIncidentLogAnalysis, type SiemAnalysisResult } from '../../hooks/useSecOps';
import { useSignalRRefresh } from '../../hooks/useSignalR';
import { EmptyState } from '../../components/EmptyState';

interface Incident {
  id: string;
  title: string;
  severity: string;
  status: string;
  assigned_to: string;
  alert_count: number;
  opened_at: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-yellow-100 text-yellow-700',
  contained: 'bg-orange-100 text-orange-700',
  eradicated: 'bg-teal-100 text-teal-700',
  recovered: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-600',
  post_incident: 'bg-purple-100 text-purple-700',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

const SEVERITY_INVESTIGATE_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  medium:   'bg-yellow-100 text-yellow-700 border-yellow-200',
  low:      'bg-blue-100 text-blue-700 border-blue-200',
};

function IncidentAnalysisModal({
  incident,
  onClose,
}: {
  incident: Incident;
  onClose: () => void;
}) {
  const [clientId, setClientId] = useState('');
  const [hoursBack, setHoursBack] = useState(72);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SiemAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleRun() {
    if (!clientId.trim()) { setError('Client ID is required'); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await runIncidentLogAnalysis({ clientId: clientId.trim(), incidentId: incident.id, hoursBack });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8 px-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <ScanSearch className="w-4 h-4 text-indigo-600" />
            Investigate Incident — Log Analysis
          </h3>
          <p className="text-sm text-gray-500 mt-0.5 truncate">{incident.title}</p>
        </div>

        <div className="p-5 space-y-4">
          {!result ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID *</label>
                  <input type="text" value={clientId} onChange={e => setClientId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="client-uuid" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Look back</label>
                  <select value={hoursBack} onChange={e => setHoursBack(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value={24}>24 hours</option>
                    <option value={48}>48 hours</option>
                    <option value={72}>72 hours</option>
                    <option value={168}>7 days</option>
                  </select>
                </div>
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
                <button onClick={handleRun} disabled={loading}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
                  {loading ? <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" />Analyzing...</> : 'Analyze Logs'}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
                <p className="text-sm text-indigo-900">{result.summary}</p>
                <p className="text-xs text-indigo-600 mt-1">{result.log_volume_analyzed.toLocaleString()} log lines analyzed</p>
              </div>

              {result.threats.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Threats ({result.threats.length})</p>
                  <div className="space-y-2">
                    {result.threats.map((t, i) => (
                      <div key={i} className={`border rounded-md p-3 text-sm ${SEVERITY_INVESTIGATE_COLORS[t.severity] ?? 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                        <div className="font-medium capitalize">{t.type} {t.mitre_technique && <span className="font-mono text-xs">({t.mitre_technique})</span>}</div>
                        <p>{t.description}</p>
                        <p className="mt-1 font-medium">Action: {t.recommended_action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.recommendations.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Recommendations</p>
                  <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                    {result.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ol>
                </div>
              )}

              <div className="flex justify-end">
                <button onClick={onClose} className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function IncidentsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', severity: 'medium', description: '', assigned_to: '' });
  const [creating, setCreating] = useState(false);
  const [investigatingIncident, setInvestigatingIncident] = useState<Incident | null>(null);

  const { data, loading, error, refresh } = useIncidents(filters);
  useSignalRRefresh('incidentUpdate', refresh);
  const incidents = (data?.incidents ?? []) as Incident[];

  function setFilter(key: string, value: string) {
    setFilters(f => value ? { ...f, [key]: value } : Object.fromEntries(Object.entries(f).filter(([k]) => k !== key)));
  }

  async function handleCreate() {
    if (!form.title) return;
    setCreating(true);
    try {
      await createIncident(form);
      setShowCreate(false);
      setForm({ title: '', severity: 'medium', description: '', assigned_to: '' });
      refresh();
    } catch { /* handled */ } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-12 bg-gray-200 rounded" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Incidents</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{data?.total ?? 0} total</span>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
            Create Incident
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap gap-3 items-center">
        <select value={filters.severity ?? ''} onChange={e => setFilter('severity', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All Severities</option>
          {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>

        <select value={filters.status ?? ''} onChange={e => setFilter('status', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
          <option value="">All Statuses</option>
          {['new', 'open', 'investigating', 'contained', 'eradicated', 'recovered', 'closed', 'post_incident'].map(s =>
            <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}</option>
          )}
        </select>

        <input type="text" placeholder="Assigned to..." value={filters.assigned_to ?? ''} onChange={e => setFilter('assigned_to', e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-40" />

        {Object.keys(filters).length > 0 && (
          <button onClick={() => setFilters({})} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Title</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Assigned To</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Alerts</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Opened</th>
              <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {incidents.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    icon={<Siren className="w-12 h-12" />}
                    title="No active incidents"
                    description="Create an incident to track and respond to security events."
                  />
                </td>
              </tr>
            )}
            {incidents.map(inc => (
              <tr key={inc.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}>
                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{inc.title}</p>
                </td>
                <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}><Badge value={inc.severity} colors={SEVERITY_COLORS} /></td>
                <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}><Badge value={inc.status} colors={STATUS_COLORS} /></td>
                <td className="px-4 py-4 text-sm text-gray-700 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}>{inc.assigned_to || 'Unassigned'}</td>
                <td className="px-4 py-4 text-sm text-gray-700 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}>{inc.alert_count ?? 0}</td>
                <td className="px-4 py-4 text-xs text-gray-500 cursor-pointer" onClick={() => navigate(`/secops/incidents/${inc.id}`)}>{inc.opened_at ? new Date(inc.opened_at).toLocaleString() : '-'}</td>
                <td className="px-4 py-4">
                  <button
                    onClick={e => { e.stopPropagation(); setInvestigatingIncident(inc); }}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                    title="Analyze logs for this incident"
                  >
                    <ScanSearch className="w-3.5 h-3.5" />
                    Investigate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Investigate Incident Modal */}
      {investigatingIncident && (
        <IncidentAnalysisModal
          incident={investigatingIncident}
          onClose={() => setInvestigatingIncident(null)}
        />
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Incident</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Incident title..." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {['critical', 'high', 'medium', 'low'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <input type="text" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Assignee..." />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={4} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Describe the incident..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreate} disabled={creating || !form.title}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                {creating ? 'Creating...' : 'Create Incident'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
